"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useCheckoutDelivery = useCheckoutDelivery;
var CheckoutStepChanged = _interopRequireWildcard(require("@ori-events/checkout-step-changed"));
var _checkoutUtils = require("@ori/checkout-utils");
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _presentationHooks = require("@ori/presentation-hooks");
var _shoppingContext = require("@ori/shopping-context");
var _react = require("react");
var _useDeliveryOptionsQuery = require("../../api/useDeliveryOptionsQuery");
var _useSelectedDeliveryQuery = require("../../api/useSelectedDeliveryQuery");
var _constants = require("../../constants");
var _contexts = require("../../contexts");
var _useAnalytics = require("../../hooks/useAnalytics");
var _useShippingOfferInDeliveryStep = require("../../hooks/useShippingOfferInDeliveryStep");
var _logger = require("../../logger");
var _models = require("../../models");
var _utils = require("../../utils");
var _scrollToElement = require("../../utils/scrollToElement");
var _usePickupPointsDialog = require("../DeliveryOptions/usePickupPointsDialog");
function _interopRequireWildcard(e, t) {
  if ("function" == typeof WeakMap) var r = new WeakMap(),
    n = new WeakMap();
  return (_interopRequireWildcard = function (e, t) {
    if (!t && e && e.__esModule) return e;
    var o,
      i,
      f = {
        __proto__: null,
        default: e
      };
    if (null === e || "object" != typeof e && "function" != typeof e) return f;
    if (o = t ? n : r) {
      if (o.has(e)) return o.get(e);
      o.set(e, f);
    }
    for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]);
    return f;
  })(e, t);
}
/** Checks that the same delivery method is selected in the main method switch as in the last selected item. */
function isSameMainDeliveryMethod(selectedItem, mainDeliveryMethod) {
  if (!selectedItem) {
    return false;
  }
  return mainDeliveryMethod === _models.DeliveryMethod.Home && selectedItem.deliveryMethod === _models.DeliveryMethod.Home || mainDeliveryMethod === _models.DeliveryMethod.PickupPoint && selectedItem.deliveryMethod !== _models.DeliveryMethod.Home;
}
function useCheckoutDelivery() {
  const {
    billingState,
    customerId,
    isFlatSelection: rawIsFlatSelection = false,
    isSimplifiedCheckout = false,
    isSubscriptionsPage,
    onContinueButtonClick: onContinueClick = () => {},
    onDeliveryAddressChange,
    showContinueButton = false
  } = (0, _contexts.useCheckoutDeliveryContext)();
  const isMobile = (0, _presentationHooks.useIsMobile)();
  const apiClient = (0, _globalApiClientReact.useApiClient)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  // flatter UI switch for simplified checkout based on feature toggle
  const isFlatSelection = rawIsFlatSelection && isSimplifiedCheckout;
  // registrationId - pending registration process identification
  const {
    shoppingContext: {
      isDeliveryChannelSelected,
      registrationId
    } = {}
  } = (0, _shoppingContext.useShoppingContext)();
  const {
    currentCourierName,
    isAddingNewDelivery,
    originalCourierName,
    originalSelectedDeliveryItem
  } = (0, _useAnalytics.useAnalytics)();
  // isSelectedDeliveryInitialized - check for the first load of delivery
  const isSelectedDeliveryInitialized = (0, _react.useRef)(false);
  // selectedItem - current item selected on main delivery page, invalid item returns null
  const [selectedItem, setSelectedItem] = (0, _react.useState)(null);
  // last selected items for each main delivery method (home, pickup point) - used for flat selection UI
  const [selectedItemHome, setSelectedItemHome] = (0, _react.useState)(null);
  const [selectedItemPickup, setSelectedItemPickup] = (0, _react.useState)(null);
  // isSelectedAddressEdited - check if address selected on main delivery was edited in delivery options dialog
  const [isSelectedAddressEdited, setIsSelectedAddressEdited] = (0, _react.useState)(false);
  // isRefreshPending - request to change delivery address or courier was triggered and is pending
  const [isRefreshPending, setIsRefreshPending] = (0, _react.useState)(false);
  // isDeliveryChanged - check if delivery address or channel was changed to request updated data
  const [isDeliveryChanged, setIsDeliveryChanged] = (0, _react.useState)(false);
  // activeCourier is preselection of courier to show optimistic UI to avoid delay in selection
  const [activeCourier, setActiveCourier] = (0, _react.useState)(0);
  // delivery options dialog visibility indicator
  const [isDeliveryOptionsOpen, setIsDeliveryOptionsOpen] = (0, _react.useState)(false);
  // when true, the addresses tab is displayed, for flat selection UI should always be true
  const [isDisplayingAddresses, setIsDisplayingAddresses] = (0, _react.useState)(isFlatSelection);
  // this state would normally be defined in useSuggestions hook, but it is needed already here for flat selection UI
  // 1st suggestions page has to be loaded so that the value can be set, until then, this has the undefined value
  const [isSuggestionAvailable, setIsSuggestionAvailable] = (0, _react.useState)(undefined);

  // TODO -- remove filtering logic later
  // current address tab filter - all addresses (default) is always used in flat selection UI
  const [activeAddressFilter, setActiveAddressFilter] = (0, _react.useState)(undefined);
  // current history tab filter - not used in flat selection UI
  const [activeFilter, setActiveFilter] = (0, _react.useState)(undefined);
  const onDeliveryOptionsQuerySuccess = (0, _react.useCallback)(deliveryOptionsData => {
    if (!deliveryOptionsData) {
      return;
    }
    if (deliveryOptionsData.filters.length > 0) {
      if (activeFilter === undefined) {
        setActiveFilter(deliveryOptionsData.filters[0]);
      }
    } else {
      setIsDisplayingAddresses(true);
    }
    if (typeof deliveryOptionsData.addressFilters[0] !== 'undefined' && activeAddressFilter === undefined) {
      (0, _logger.logDebug)('Setting active address filter:', deliveryOptionsData.addressFilters[0]);
      setActiveAddressFilter(deliveryOptionsData.addressFilters[0]);
    }
  }, [activeFilter, setActiveFilter, activeAddressFilter, setActiveAddressFilter]);

  // loads current delivery options configuration
  const {
    isDeliveryOptionsLoading,
    deliveryOptions,
    mutateDeliveryOptions
  } = (0, _useDeliveryOptionsQuery.useDeliveryOptionsQuery)({
    isEnabled: isFlatSelection || isDeliveryOptionsOpen,
    onSuccess: onDeliveryOptionsQuerySuccess
  });
  const {
    addressFilters = [_constants.DEFAULT_ADDRESS_FILTER],
    filters = [_constants.DEFAULT_HISTORY_FILTER],
    isAddHomeDeliveryAddressAllowed = false,
    isMapEnabled = false,
    isPickUpAvailable = false
  } = deliveryOptions ?? {};
  const onSelectedDeliveryQuerySuccess = (0, _react.useCallback)(lastDeliveryItem => {
    setIsRefreshPending(false);
    if (!lastDeliveryItem) {
      (0, _logger.logDebug)('No last delivery item found.');
      return;
    }
    if (!lastDeliveryItem.isLastUsed) {
      (0, _logger.logDebug)('Item returned by last delivery endpoint was not used in last order & will not be preselected at this point.');
      return;
    }
    if (lastDeliveryItem.addressId !== selectedItem?.addressId) {
      (0, _logger.logDebug)('Syncing previously selected delivery item to local state, addressId', lastDeliveryItem.addressId);
    }
    setSelectedItem(lastDeliveryItem);
    if (lastDeliveryItem.deliveryMethod === _models.AddressItemDeliveryMethod.Home) {
      setSelectedItemHome(lastDeliveryItem);
      if (lastDeliveryItem.deliveryChannelId !== activeCourier) {
        (0, _logger.logDebug)('Syncing previously selected courier to local state, deliveryChannelId', lastDeliveryItem.deliveryChannelId);
      }
      setActiveCourier(lastDeliveryItem.deliveryChannelId);
    }
    if (lastDeliveryItem.deliveryMethod !== _models.AddressItemDeliveryMethod.Home) {
      setSelectedItemPickup(lastDeliveryItem);
    }
  }, [activeCourier, selectedItem]);
  const {
    isSelectedDeliveryLoading,
    lastDeliveryItem,
    mutateSelectedDelivery
  } = (0, _useSelectedDeliveryQuery.useSelectedDeliveryQuery)({
    onSuccess: onSelectedDeliveryQuerySuccess
  });
  const isLoading = isDeliveryOptionsLoading || isSelectedDeliveryLoading || isRefreshPending;

  /*
  Flat selection UI
  Main delivery method switch should be displayed when more than 1 delivery method is available.
  Home delivery should always be available, but address modification might be disabled on some markets (the `isAddHomeDeliveryAddressAllowed` switch).
  Displayed for both empty selection and / or with already selected item.
  In other cases, the remaining method is automatically preselected.
  */
  const [mainDeliveryMethod, setMainDeliveryMethod] = (0, _react.useState)(undefined);
  const rootRef = (0, _react.useRef)(null);
  const toggleDeliveryOptionsDialog = (0, _react.useCallback)(() => {
    setIsDeliveryOptionsOpen(prevState => !prevState);
    if (isSelectedAddressEdited) {
      setIsSelectedAddressEdited(false);
      void mutateSelectedDelivery();
    }
  }, [isSelectedAddressEdited, mutateSelectedDelivery]);
  const changeSelectedDelivery = (0, _react.useCallback)(async activeItem => {
    const {
      addressId,
      deliveryMethod,
      deliveryChannelId
    } = activeItem;
    (0, _logger.logDebug)('changeSelectedDelivery()', {
      addressId,
      deliveryMethod,
      deliveryChannelId
    });
    try {
      setSelectedItem(activeItem);
      if (deliveryMethod === _models.AddressItemDeliveryMethod.Home) {
        setActiveCourier(deliveryChannelId);
        setSelectedItemHome(activeItem);
        (0, _logger.logDebug)('changeSelectedDelivery(): active courier was set to', deliveryChannelId);
        const isAddressSelected = addressId === selectedItem?.addressId;
        const isChannelSelected = deliveryChannelId === activeCourier;
        (0, _logger.logDebug)('changeSelectedDelivery()', {
          isAddressSelected,
          isChannelSelected
        });
        if (!isSubscriptionsPage) {
          await (0, _utils.changeActiveDelivery)({
            addressId,
            deliveryChannelId,
            force: !isDeliveryChannelSelected,
            isAddressSelected,
            isChannelSelected,
            registrationId,
            setIsDeliveryChanged
          });
        }
      } else {
        setSelectedItemPickup(activeItem);
        if (!isSubscriptionsPage) {
          await (0, _utils.changeActiveDelivery)({
            deliveryChannelId,
            force: !isDeliveryChannelSelected,
            setIsDeliveryChanged
          });
        }
      }
      onDeliveryAddressChange?.();
      (0, _scrollToElement.scrollToElement)(rootRef, isMobile);
    } catch (error) {
      (0, _checkoutUtils.showErrorNotification)();
      _logger.mainLogger.error(error, {
        action: `Changing active delivery item: ${activeItem}`
      });
      void mutateSelectedDelivery();
    }
  }, [activeCourier, isDeliveryChannelSelected, isMobile, isSubscriptionsPage, mutateSelectedDelivery, onDeliveryAddressChange, registrationId, selectedItem?.addressId]);
  const onDeliveryItemActivation = (0, _react.useCallback)(activeItem => {
    // avoid change if combination of address and courier is already selected
    const isDeliveryCombinationSelected = activeItem.addressId === selectedItem?.addressId && activeItem.deliveryChannelId === selectedItem?.deliveryChannelId;
    (0, _logger.logDebug)(`onDeliveryItemActivation ${activeItem.addressId}`, activeItem);
    if (!isDeliveryCombinationSelected) {
      (0, _logger.logDebug)('Setting active address item:', activeItem);
      void changeSelectedDelivery(activeItem).catch(() => (0, _checkoutUtils.showErrorNotification)());
      if (activeItem.addressId !== selectedItem?.addressId) {
        setIsSelectedAddressEdited(false);
      }
    }
    if (!isFlatSelection) {
      toggleDeliveryOptionsDialog();
    }
  }, [changeSelectedDelivery, isFlatSelection, selectedItem, toggleDeliveryOptionsDialog]);
  const updateSubscriptionAddress = (0, _react.useCallback)(async () => {
    try {
      await (0, _globalApiClientReact.updateCustomerSubscriptionProfile)(apiClient, customerId, {
        addressId: selectedItem?.addressId,
        customerId,
        deliveryChannelId: selectedItem?.deliveryMethod === _models.AddressItemDeliveryMethod.Home ? activeCourier : Number(selectedItem?.deliveryChannelId)
      }, {
        [_fetching.TENANT_CONTEXT]: tenant
      });
      window.location.replace(_constants.SUBSCRIPTIONS_PAGE_LINK);
    } catch (error) {
      (0, _checkoutUtils.showErrorNotification)();
    }
  }, [activeCourier, apiClient, customerId, selectedItem, tenant]);

  // flat selection UI - set mainDeliveryMethod after both delivery options configuration and last delivery selection item are loaded
  (0, _react.useEffect)(() => {
    if (!isFlatSelection || isSelectedDeliveryLoading || !deliveryOptions || mainDeliveryMethod) {
      return;
    }
    if (lastDeliveryItem) {
      if (isSubscriptionsPage && !selectedItem) {
        onSelectedDeliveryQuerySuccess(lastDeliveryItem);
      }
      if (lastDeliveryItem.deliveryMethod === _models.AddressItemDeliveryMethod.Home) {
        setMainDeliveryMethod(_models.AddressItemDeliveryMethod.Home);
        setSelectedItemHome(lastDeliveryItem);
      }
      if (lastDeliveryItem.deliveryMethod !== _models.AddressItemDeliveryMethod.Home && isPickUpAvailable) {
        setMainDeliveryMethod(_models.AddressItemDeliveryMethod.PickupPoint);
        setSelectedItemPickup(lastDeliveryItem);
      }
    } else {
      setMainDeliveryMethod(_models.AddressItemDeliveryMethod.Home);
    }
  }, [deliveryOptions, isFlatSelection, isPickUpAvailable, isSelectedDeliveryLoading, isSubscriptionsPage, lastDeliveryItem, mainDeliveryMethod, onSelectedDeliveryQuerySuccess, selectedItem]);

  // flat selection UI - apply last local selected item when switching between home delivery and pickup point selection sections
  (0, _react.useEffect)(() => {
    if (!isFlatSelection || isLoading || isDeliveryChanged || !isSelectedDeliveryInitialized.current || !mainDeliveryMethod) {
      return;
    }
    if (mainDeliveryMethod === _models.AddressItemDeliveryMethod.Home && selectedItemHome !== null && selectedItem !== selectedItemHome) {
      // apply last pickup point selected in current user session when switching to pickup point selection section
      (0, _logger.logDebug)('Applying last home delivery item:', selectedItemHome);
      onDeliveryItemActivation(selectedItemHome);
    } else if (mainDeliveryMethod !== _models.AddressItemDeliveryMethod.Home && selectedItemPickup !== null && selectedItem !== selectedItemPickup) {
      // apply last pickup point selected in current user session when switching to pickup point selection section
      (0, _logger.logDebug)('Applying last session pickup point item:', selectedItemPickup);
      onDeliveryItemActivation(selectedItemPickup);
    } else if (mainDeliveryMethod !== _models.AddressItemDeliveryMethod.Home && selectedItemPickup === null && selectedItem !== null && selectedItem.deliveryMethod !== _models.AddressItemDeliveryMethod.Home) {
      // set previously selected item into local state when comming to pickup section 1st time in given user session
      (0, _logger.logDebug)('Applying last selected pickup point item:', selectedItem);
      setSelectedItemPickup(selectedItem);
    }
  }, [isDeliveryChanged, isFlatSelection, isLoading, mainDeliveryMethod, onDeliveryItemActivation, selectedItem, selectedItemHome, selectedItemPickup]);
  const [wasOpened, setWasOpened] = (0, _react.useState)(false);
  const deliveryChannelId = selectedItem?.deliveryChannelId ?? 0;
  const {
    isShippingOfferDialogOpen,
    onShippingOfferBannerClick,
    setIsShippingOfferDialogOpen,
    shippingOfferDialogProps
  } = (0, _useShippingOfferInDeliveryStep.useShippingOfferInDeliveryStep)(deliveryChannelId, mutateSelectedDelivery);
  (0, _react.useEffect)(() => {
    const refreshDelivery = event => {
      if (event.detail.pageId === CheckoutStepChanged.CheckoutStep.Delivery) {
        void mutateSelectedDelivery();
      } else {
        // analytics
        // check the previous selections when going away from delivery step
        if (originalSelectedDeliveryItem.current === selectedItem) {
          (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.DeliverySelection, _models.MetaHierarchy.PreviousSelection]);
          const courierName = currentCourierName.current;
          if (Boolean(courierName) && originalCourierName.current === courierName) {
            (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.HomeDeliveryCourierSelection, _models.MetaHierarchy.PreviouslyUsedCourier, courierName]);
          }
        }
        // reset the analytics context
        // eslint-disable-next-line react-compiler/react-compiler -- want to reset the value here
        currentCourierName.current = undefined;
        originalCourierName.current = undefined;
        originalSelectedDeliveryItem.current = undefined;
      }
      setIsDeliveryOptionsOpen(false);
      setIsShippingOfferDialogOpen(false);
    };
    if (isDeliveryChanged) {
      void mutateSelectedDelivery();
      setIsDeliveryChanged(false);
    }
    if (!isDeliveryChanged && !isSelectedDeliveryLoading && lastDeliveryItem && lastDeliveryItem.isLastUsed && !isSubscriptionsPage) {
      if (!isSelectedDeliveryInitialized.current) {
        onDeliveryAddressChange?.();
      }
      // startup last delivery selection application into local states
      isSelectedDeliveryInitialized.current = true;
      setSelectedItem(lastDeliveryItem);
      if (lastDeliveryItem.addressId !== undefined) {
        setActiveCourier(lastDeliveryItem.deliveryChannelId);
      }
    }
    window.addEventListener(CheckoutStepChanged.Name, refreshDelivery);
    return () => {
      window.removeEventListener(CheckoutStepChanged.Name, refreshDelivery);
    };
  }, [currentCourierName, isDeliveryChanged, isSelectedDeliveryLoading, isSubscriptionsPage, lastDeliveryItem, mutateSelectedDelivery, onDeliveryAddressChange, originalCourierName, originalSelectedDeliveryItem, selectedItem, setIsShippingOfferDialogOpen]);

  /*
  Opens delivery options dialog when no delivery item was selected "manually" by the user before.
  Can be dropped when simplified checkout is used or flat selection UI is enabled everywhere.
  */
  if (!(isSimplifiedCheckout || isFlatSelection) && !isDeliveryOptionsOpen && !wasOpened && !isSelectedDeliveryLoading && !lastDeliveryItem?.isLastUsed) {
    setTimeout(() => {
      setWasOpened(true);
      setIsDeliveryOptionsOpen(true);
    }, 500);
  }
  const onFindBoxClick = (0, _react.useCallback)(() => {
    isAddingNewDelivery.current = true;
    toggleDeliveryOptionsDialog();
  }, [isAddingNewDelivery, toggleDeliveryOptionsDialog]);
  const onChangeButtonClick = (0, _react.useCallback)(() => {
    isAddingNewDelivery.current = false;
    toggleDeliveryOptionsDialog();
  }, [isAddingNewDelivery, toggleDeliveryOptionsDialog]);
  const isDeliveryOptionsLoaded = !isDeliveryOptionsLoading && Boolean(deliveryOptions);
  const isContinueButtonDisabled = isSelectedDeliveryLoading || !showContinueButton ||
  // TODO -- This is teoretically fine, but prevents the solution of calling storing data on continue button click.
  // Correct approach could be to keep it here w/o the check, but apply the last selection on continue button click,
  // then wait with displaying the billing address form until the isDeliveryChannelSelected is set to true
  // in shopping context.
  // !isDeliveryChannelSelected ||
  !isSameMainDeliveryMethod(selectedItem, mainDeliveryMethod);
  const onContinueButtonClick = (0, _react.useCallback)(() => {
    (0, _logger.logDebug)('Clicked continue button, isDeliveryChannelSelected:', isDeliveryChannelSelected);
    if (!isDeliveryChannelSelected && selectedItem !== null) {
      void changeSelectedDelivery(selectedItem).then(() => {
        (0, _logger.logDebug)('Selected item successfully applied. Enabling billing address section ...');
        onContinueClick();
      });
    } else {
      // Enables billing address section as a side effect.
      onContinueClick();
    }
  }, [changeSelectedDelivery, isDeliveryChannelSelected, onContinueClick, selectedItem]);
  const deliveryOptionsDialogProps = {
    activeAddressFilter,
    activeFilter,
    addressFilters,
    filters,
    isAddHomeDeliveryAddressAllowed,
    isDeliveryOptionsLoaded,
    isDeliveryOptionsLoading,
    isDisplayingAddresses,
    isFlatSelection,
    isMapEnabled,
    isPickUpAvailable,
    isRefreshPending,
    isSelectedDeliveryLoading,
    isSuggestionAvailable,
    lastDeliveryItemFee: lastDeliveryItem?.deliveryFee,
    lastDeliveryItemMethod: lastDeliveryItem?.deliveryMethod,
    mutateDeliveryOptions,
    onDeliveryItemActivation,
    selectedItem,
    selectedItemHome,
    selectedItemPickup,
    setActiveAddressFilter,
    setActiveFilter,
    setIsDisplayingAddresses,
    setIsSelectedAddressEdited,
    setIsSuggestionAvailable,
    setSelectedItemHome,
    toggleDeliveryOptionsDialog
  };
  const mainDeliveryMethodSwitchProps = {
    isLoading,
    lastDeliveryItem,
    mainDeliveryMethod,
    setMainDeliveryMethod
  };
  const {
    addressId = 0,
    hintMessages = [],
    outOfStockProducts,
    outOfStockQuantity
  } = selectedItemHome ?? {};
  const homeDeliveryAddressSelectionProps = {
    activeAddressFilter,
    activeFilter,
    addressFilters,
    courierSelectionProps: {
      activeCourier,
      currentAddressId: addressId,
      hintMessages,
      isRefreshPending,
      isSelectedDeliveryLoading,
      onShippingOfferBannerClick,
      outOfStockQuantity: (0, _utils.getOutOfStockQuantity)({
        outOfStockQuantity,
        outOfStockProducts
      }),
      setActiveCourier,
      setIsDeliveryChanged,
      setIsRefreshPending
    },
    filters,
    isAddHomeDeliveryAddressAllowed,
    isDeliveryOptionsLoading,
    isDisplayingAddresses,
    isFlatSelection,
    isRefreshPending,
    isSelectedDeliveryLoading,
    isSuggestionAvailable,
    onDeliveryItemActivation,
    selectedItem,
    selectedItemHome,
    setActiveAddressFilter,
    setActiveFilter,
    setIsDisplayingAddresses,
    setIsSuggestionAvailable,
    setSelectedItemHome
  };
  const {
    isFindPickupPointsOpen,
    togglePickupPointsDialog
  } = (0, _usePickupPointsDialog.usePickupPointsDialog)();
  const pickupPointSelectionProps = {
    isDeliveryOptionsLoaded,
    isFindPickupPointsOpen,
    isLoading,
    isMapEnabled,
    onDeliveryItemActivation,
    onShippingOfferBannerClick,
    selectedItem,
    selectedItemPickup,
    togglePickupPointsDialog
  };
  return {
    activeAddressFilter,
    activeCourier,
    activeFilter,
    addressFilters,
    changeSelectedDelivery,
    deliveryOptionsDialogProps,
    filters,
    homeDeliveryAddressSelectionProps,
    isContinueButtonDisabled,
    isDeliveryOptionsLoaded,
    isDeliveryOptionsLoading,
    isDeliveryOptionsOpen,
    isDisplayingAddresses,
    isFlatSelection,
    isFlatSelectionEditMode: billingState !== 'enabled' || !isDeliveryChannelSelected,
    isLoading,
    isMainDeliveryMethodSwitchDisplayed: isPickUpAvailable,
    isMapEnabled,
    isRefreshPending,
    isSelectedDeliveryInitialized,
    isSelectedDeliveryLoading,
    isShippingOfferDialogOpen,
    isSimplifiedCheckout,
    isSuggestionAvailable,
    lastDeliveryItem,
    mainDeliveryMethod,
    mainDeliveryMethodSwitchProps,
    mutateDeliveryOptions,
    onDeliveryItemActivation,
    onChangeButtonClick,
    onContinueButtonClick,
    onDeliveryAddressChange,
    onFindBoxClick,
    onShippingOfferBannerClick,
    pickupPointSelectionProps,
    rootRef,
    selectedItem,
    selectedItemHome,
    selectedItemPickup,
    setActiveCourier,
    setActiveAddressFilter,
    setActiveFilter,
    setIsDeliveryChanged,
    setIsDeliveryOptionsOpen,
    setIsDisplayingAddresses,
    setIsRefreshPending,
    setIsSelectedAddressEdited,
    setIsSuggestionAvailable,
    setIsShippingOfferDialogOpen,
    setMainDeliveryMethod,
    setSelectedItemHome,
    shippingOfferDialogProps,
    showContinueButton,
    updateSubscriptionAddress
  };
}